import { workbenchRoutes } from '@affine/core/desktop/workbench-router';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { matchRoutes } from 'react-router-dom';

import { useWorkspaceSidebarVisibility } from './use-workspace-sidebar-visibility';

/** Remember local rail visibility without writing the workbench history. */
export function useWorkspaceNavigation({
  pathname,
  fromBrowser,
  viewId,
  workspaceId,
  transient,
  openAll,
}: {
  pathname: string;
  /** The view adopted this location from the browser URL (load, history). */
  fromBrowser: boolean;
  viewId: string;
  workspaceId: string;
  /**
   * On a narrow window the document sidebar is a passing sheet: Escape and any step back into
   * the document put it away. Otherwise it stays until the user collapses it.
   */
  transient: boolean;
  openAll: () => void;
}) {
  const [storedOpen, setStoredOpen] = useWorkspaceSidebarVisibility('overlay');
  const [allSidebarOpen, setAllSidebarOpen] = useWorkspaceSidebarVisibility(
    'all',
    false
  );
  const previousRoute = useRef({ pathname, workspaceId, viewId });
  const before = previousRoute.current;
  const sameView =
    before.workspaceId === workspaceId && before.viewId === viewId;
  // Only an in-app step from /all carries its sidebar into the document. A
  // browser-sourced URL replaces the view's initial /all entry on every load;
  // that /all was never shown and says nothing about the user's choice.
  const leavingAll =
    sameView &&
    !fromBrowser &&
    before.pathname === '/all' &&
    pathname !== '/all';
  const workspaceButtonRef = useRef<HTMLButtonElement>(null);
  const documentsPanelRef = useRef<HTMLDivElement>(null);
  // The route owns navigation even while the next DetailPage resets and then
  // restores GlobalContext. Keep the portal mounted across document changes;
  // static app routes must still use their normal Workspace action.
  const route = matchRoutes(workbenchRoutes, { pathname })?.at(-1);
  const hasActiveDocument = route?.route.path === '/:pageId';
  const documentsOpen =
    hasActiveDocument && (leavingAll ? allSidebarOpen : storedOpen);
  const showDocumentsNavigation = hasActiveDocument || pathname === '/all';
  const navigationOpen = pathname === '/all' ? allSidebarOpen : documentsOpen;

  useLayoutEffect(() => {
    previousRoute.current = { pathname, workspaceId, viewId };
    if (hasActiveDocument && leavingAll) setStoredOpen(allSidebarOpen);
  }, [
    allSidebarOpen,
    hasActiveDocument,
    leavingAll,
    pathname,
    setStoredOpen,
    viewId,
    workspaceId,
  ]);

  // A sidebar that is still open from last time leaves the focus with the document; the passing
  // sheet and every opening take it.
  const wasDocumentsOpen = useRef(!transient && documentsOpen);
  useEffect(() => {
    const opened = documentsOpen && !wasDocumentsOpen.current;
    wasDocumentsOpen.current = documentsOpen;
    if (opened) documentsPanelRef.current?.focus({ preventScroll: true });
  }, [documentsOpen]);

  const closeDocuments = useCallback(() => {
    if (pathname === '/all') setAllSidebarOpen(false);
    else setStoredOpen(false);
    workspaceButtonRef.current?.focus({ preventScroll: true });
  }, [pathname, setAllSidebarOpen, setStoredOpen]);

  // A document gesture keeps its own focus and pointer capture. Escape/Close
  // still return focus to the rail, but editing must not focus the launcher.
  const onDocumentInteract = useCallback(
    () => setStoredOpen(false),
    [setStoredOpen]
  );

  // Escape puts away what passes: the sheet of a narrow window.
  const escapes = documentsOpen && transient;

  const onDocumentsKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      if (escapes && event.key === 'Escape' && !event.defaultPrevented) {
        // Handle navigation-owned focus before document-level editor shortcuts.
        // Child menus still get the bubbling event first and may consume it.
        event.preventDefault();
        event.stopPropagation();
        closeDocuments();
      }
    },
    [closeDocuments, escapes]
  );

  useEffect(() => {
    if (!escapes) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      // A nested menu/dialog gets Escape first; closing it must not also
      // dismiss its parent navigation.
      if (event.key === 'Escape' && !event.defaultPrevented) {
        event.preventDefault();
        closeDocuments();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeDocuments, escapes]);

  // The entry of the area on screen puts its sidebar away and brings it back; from any other
  // area it leads to the workspace's documents.
  const onWorkspaceClick = useCallback(() => {
    if (hasActiveDocument) {
      if (documentsOpen) closeDocuments();
      else setStoredOpen(true);
    } else if (pathname === '/all') {
      setAllSidebarOpen(!allSidebarOpen);
    } else {
      setStoredOpen(false);
      setAllSidebarOpen(true);
      openAll();
    }
  }, [
    allSidebarOpen,
    closeDocuments,
    documentsOpen,
    hasActiveDocument,
    openAll,
    pathname,
    setAllSidebarOpen,
    setStoredOpen,
  ]);

  return {
    hasActiveDocument,
    showDocumentsNavigation,
    navigationOpen,
    documentsOpen,
    documentsPanelRef,
    workspaceButtonRef,
    setDocumentsOpen: setStoredOpen,
    onDocumentInteract,
    onDocumentsKeyDown,
    onWorkspaceClick,
  };
}
